<template>
  <div
    class="create-cita relative overflow-hidden max-w-6xl mx-auto p-6 sm:p-8 space-y-6 border border-amber-100/80 rounded-3xl bg-gradient-to-br from-amber-50 via-white to-rose-50 shadow-2xl"
    style="--accent:#ea580c;--accent-strong:#c2410c;--accent-soft:#ffedd5;--ink:#0f172a;--muted:#64748b;"
  >
    <div aria-hidden="true" class="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-amber-200/50 blur-3xl"></div>
    <div aria-hidden="true" class="pointer-events-none absolute -bottom-28 -left-16 h-56 w-56 rounded-full bg-rose-200/40 blur-3xl"></div>
    <header class="relative z-10 space-y-4">
      <div class="flex flex-wrap items-center gap-2 text-[0.65rem] font-semibold uppercase tracking-[0.35em] text-amber-600">
        <span class="px-3 py-1 rounded-full bg-amber-100/80">Panel admin</span>
        <span class="px-3 py-1 rounded-full border border-amber-200/70 bg-white/80 text-slate-600">Citas presenciales</span>
      </div>
      <div class="space-y-2">
        <h2 class="serif-title text-4xl md:text-5xl font-semibold text-slate-900">Crear nueva cita</h2>
        <p class="text-sm text-slate-600 max-w-2xl">
          Todo lo que registres aquí se sincroniza con la vista global y tu agenda personal.
        </p>
      </div>
      <div class="flex flex-wrap gap-3 text-xs font-semibold text-slate-500">
        <span class="px-3 py-1 rounded-full bg-white/80 border border-slate-200">1 Paciente</span>
        <span class="px-3 py-1 rounded-full bg-white/80 border border-slate-200">2 Servicios</span>
        <span class="px-3 py-1 rounded-full bg-white/80 border border-slate-200">3 Agenda</span>
        <span class="px-3 py-1 rounded-full bg-white/80 border border-slate-200">4 Cobro</span>
      </div>
    </header>

    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <form
        id="form-crear-cita"
        @submit.prevent="emitirCitaNueva"
        class="space-y-6"
      >
        <section class="panel-card space-y-3">
          <label class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 block">
            Paciente
          </label>
          <input
            type="text"
            v-model="filtroPaciente"
            @input="buscarPacientes"
            placeholder="Busca por nombre o apellido..."
            class="w-full rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
          />
          <ul
            v-if="pacientesFiltrados.length"
            class="bg-white border border-slate-200 rounded-xl mt-2 shadow-lg max-h-44 overflow-y-auto relative"
          >
            <li
              v-for="pac in pacientesFiltrados"
              :key="pac.id"
              @click="seleccionarPaciente(pac)"
              class="px-3 py-2.5 text-sm text-slate-700 hover:bg-amber-50 cursor-pointer"
            >
              {{ pac.nombre }} {{ pac.apellido || "" }}
            </li>
          </ul>
          <p v-if="formCita.pacienteNombre" class="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
            Seleccionado: {{ formCita.pacienteNombre }}
          </p>
        </section>

        <section class="panel-card space-y-3">
          <div class="flex items-center justify-between">
            <h3 class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Tratamientos</h3>
            <button
              type="button"
              data-modal-target="modal-tratamientos"
              data-modal-toggle="modal-tratamientos"
              class="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 hover:text-amber-700"
            >
              Ver populares
            </button>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
            <label
              v-for="servicio in serviciosDisponibles"
              :key="servicio.id"
              class="rounded-xl border p-3 flex gap-3 items-start transition hover:shadow-sm"
              :class="formCita.servicios.includes(servicio)
                ? 'border-amber-300 bg-amber-50/70'
                : 'border-slate-200 bg-white/70 hover:border-amber-200 hover:bg-amber-50/40'"
            >
              <input
                type="checkbox"
                :value="servicio"
                v-model="formCita.servicios"
                @change="recalcularDuracion"
                class="mt-1 accent-amber-600"
              />
              <div>
                <p class="text-sm font-semibold text-slate-800">
                  {{ servicio.nombre }}
                </p>
                <p class="text-xs text-slate-500">
                  {{ servicio.duracion || 0 }} min ·
                  {{ formatearMonto(servicio.precio || servicio.precioFinal || 0) }}
                </p>
              </div>
            </label>
          </div>
        </section>

        <section class="panel-card space-y-4">
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <label class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 block mb-2">
                Fecha
              </label>
              <input
                type="date"
                v-model="formCita.fechaCita"
                @change="recalcularDuracion"
                class="w-full rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
                required
              />
            </div>
            <div>
              <label class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 block mb-2">
                Hora
              </label>
              <select
                v-model="formCita.hora"
                class="w-full rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
                required
              >
                <option value="">Selecciona una hora</option>
                <option v-for="hora in horasDisponibles" :key="hora" :value="hora">
                  {{ formatearHora24aAMPM(hora) }}
                </option>
              </select>
            </div>
            <div class="md:col-span-2">
              <label class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 block mb-2">
                Asignar esteticista
              </label>
              <select
                v-model="formCita.enfermero"
                class="w-full rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
              >
                <option value="">Automático (según disponibilidad)</option>
                <option
                  v-for="enf in esteticistasDisponibles"
                  :key="enf.id"
                  :value="enf.id"
                >
                  {{ enf.nombre }}
                </option>
              </select>
              <p class="text-xs text-slate-500 mt-2">
                Selecciona fecha y hora para ver disponibilidad real.
              </p>
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 block mb-2">
              Nota interna / agenda personal
            </label>
            <textarea
              v-model="formCita.notaInterna"
              rows="3"
              placeholder="Ej. Confirmar alergias, traer receta, recordatorio para agenda personal..."
              class="w-full rounded-xl border border-slate-200 bg-white/80 px-3 py-2.5 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
            ></textarea>
          </div>

          <div class="grid gap-3 md:grid-cols-3">
            <label
              class="flex items-start gap-3 text-sm text-slate-700 border rounded-2xl p-3 bg-white/80 transition"
              :class="requierePago ? 'border-amber-300 bg-amber-50/70 shadow-sm' : 'border-slate-200'"
            >
              <input
                type="checkbox"
                v-model="requierePago"
                class="mt-1 accent-amber-600"
              />
              <span>
                <span class="font-semibold block">Generar saldo pendiente</span>
                Calcula el depósito a cobrar y crea un recordatorio en finanzas.
              </span>
            </label>
            <label
              class="flex items-start gap-3 text-sm text-slate-700 border rounded-2xl p-3 bg-white/80 transition"
              :class="formCita.sincronizarAgenda ? 'border-amber-300 bg-amber-50/70 shadow-sm' : 'border-slate-200'"
            >
              <input
                type="checkbox"
                v-model="formCita.sincronizarAgenda"
                class="mt-1 accent-amber-600"
              />
              <span>
                <span class="font-semibold block">Sincronizar agenda personal</span>
                Marca la cita en tu calendario personal para no perder seguimiento.
              </span>
            </label>
            <label
              class="flex items-start gap-3 text-sm text-slate-700 border rounded-2xl p-3 bg-white/80 transition"
              :class="formCita.enviarRecordatorio ? 'border-amber-300 bg-amber-50/70 shadow-sm' : 'border-slate-200'"
            >
              <input
                type="checkbox"
                v-model="formCita.enviarRecordatorio"
                class="mt-1 accent-amber-600"
              />
              <span>
                <span class="font-semibold block">Enviar recordatorio</span>
                Dispara un SMS/correo automático 24h antes de la cita.
              </span>
            </label>
          </div>

          <div class="rounded-2xl border border-amber-100 bg-amber-50/70 p-4 space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-[0.65rem] uppercase tracking-[0.3em] text-amber-600 font-semibold">Opcional</span>
                  <p class="text-sm font-semibold text-slate-800">
                    Depósito presencial (admin/supervisor)
                  </p>
                  <span
                    v-if="depositoConfirmado"
                    class="text-[0.65rem] uppercase tracking-[0.2em] bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-semibold"
                  >
                    Confirmado
                  </span>
                  <span
                    v-else-if="depositoPresencial > 0"
                    class="text-[0.65rem] uppercase tracking-[0.2em] bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-semibold"
                  >
                    Pendiente
                  </span>
                </div>
                <p class="text-xs text-slate-600 mt-1">
                  Este importe se registra como pago presencial en POS y reduce el saldo pendiente.
                </p>
              </div>
              <input
                v-model.number="depositoPresencial"
                type="number"
                min="0"
                step="0.01"
                class="w-32 text-right rounded-xl border border-amber-200 bg-white/90 px-3 py-2 text-sm shadow-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none"
              />
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                @click="abrirMiniPos"
                class="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-[color:var(--accent)] text-white text-sm font-semibold shadow-md hover:bg-[color:var(--accent-strong)] transition"
              >
                Abrir mini POS y registrar pago
              </button>
              <p class="text-xs text-slate-500">
                Registra el recibo ahora para confirmar el depósito.
              </p>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <button
              type="button"
              @click="$emit('cerrar')"
              class="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 font-semibold hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="px-5 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-lg hover:bg-slate-800 transition"
            >
              Guardar cita
            </button>
          </div>
        </section>
      </form>

      <aside class="panel-card space-y-4 lg:sticky lg:top-6 h-max">
        <div class="rounded-2xl bg-slate-900 text-white p-4 space-y-2 shadow-lg">
          <p class="text-[0.65rem] uppercase tracking-[0.35em] text-slate-300">Resumen rápido</p>
          <p class="serif-title text-3xl font-semibold">
            {{ formatearMonto(totalServicios) }}
          </p>
          <p class="text-xs text-slate-200">
            Saldo pendiente estimado: {{ formatearMonto(saldoPendienteEstimado) }}
          </p>
          <div class="flex flex-wrap gap-2 text-[0.7rem] font-semibold">
            <span
              v-if="depositoConfirmado"
              class="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-200"
            >
              Depósito confirmado
            </span>
            <span
              v-else-if="depositoPresencial > 0"
              class="px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-200"
            >
              Depósito pendiente
            </span>
            <span
              v-else
              class="px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-200"
            >
              Sin depósito
            </span>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white/90 p-3 text-sm text-slate-700 space-y-2">
          <p class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Paciente</p>
          <p class="font-semibold text-slate-900">
            {{ formCita.pacienteNombre || "Sin seleccionar" }}
          </p>
          <p class="text-xs text-slate-500">
            {{ pacienteSel?.telefono || "Teléfono no disponible" }}
          </p>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white/90 p-3 text-sm text-slate-700 space-y-2">
          <p class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Agenda</p>
          <p class="font-semibold text-slate-900">
            {{ formCita.fechaCita || "Sin fecha" }}
          </p>
          <p class="text-xs text-slate-500">
            {{ formCita.hora ? formatearHora24aAMPM(formCita.hora) : "Selecciona hora" }}
          </p>
          <p class="text-xs text-slate-500">
            Esteticista: {{ nombreEnfermeroSeleccionado }}
          </p>
          <div class="flex items-center gap-2 text-[0.7rem] font-semibold text-slate-500">
            <span class="px-2 py-1 rounded-full bg-slate-100">
              {{ formCita.duracion || 0 }} min
            </span>
            <span class="px-2 py-1 rounded-full bg-slate-100">
              {{ formCita.sincronizarAgenda ? "Agenda personal activa" : "Agenda personal no activa" }}
            </span>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white/90 p-3 text-sm text-slate-700 space-y-2">
          <p class="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Servicios seleccionados</p>
          <ul v-if="formCita.servicios.length" class="text-xs text-slate-600 space-y-1 max-h-28 overflow-y-auto pr-1">
            <li v-for="servicio in formCita.servicios" :key="servicio.id">
              {{ servicio.nombre }}
            </li>
          </ul>
          <p v-else class="text-xs text-slate-500">
            Añade tratamientos para calcular duración.
          </p>
          <div class="flex items-center justify-between text-xs text-slate-600">
            <span>Depósito presencial:</span>
            <span class="font-semibold text-slate-800">
              {{ formatearMonto(depositoPresencial || 0) }}
            </span>
          </div>
        </div>
      </aside>
    </div>

    <DepositCheckout
      :visible="mostrarMiniPos"
      :producto="depositoProducto"
      :metodo-inicial="depositoMetodo"
      @confirmar="confirmarDeposito"
      @cancelar="cerrarMiniPos"
    />

    <div
      id="modal-tratamientos"
      tabindex="-1"
      aria-hidden="true"
      class="fixed top-0 left-0 right-0 z-50 hidden w-full p-4 overflow-x-hidden overflow-y-auto h-[calc(100%-1rem)] max-h-full bg-black/50"
    >
      <div class="relative w-full max-w-md max-h-full mx-auto mt-20">
        <div class="relative bg-white rounded-lg shadow dark:bg-gray-100">
          <div class="flex items-start justify-between p-4 border-b rounded-t">
            <h3 class="text-xl font-semibold text-gray-800">
              Tratamientos más vistos
            </h3>
            <button
              type="button"
              class="text-gray-500 hover:text-gray-700"
              data-modal-hide="modal-tratamientos"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div class="p-4 space-y-2 max-h-64 overflow-y-auto">
            <div
              v-for="top in tratamientosMasVistos"
              :key="top.id"
              class="flex justify-between items-center text-sm text-gray-700 border-b py-2"
            >
              <span>{{ top.nombre }}</span>
              <span class="text-orange-600 font-semibold">
                {{ top.veces }} vistas
              </span>
            </div>
          </div>
          <div class="p-4 flex justify-end">
            <button
              data-modal-hide="modal-tratamientos"
              type="button"
              class="text-white bg-orange-600 hover:bg-orange-700 font-medium rounded-lg text-sm px-4 py-2"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import { obtenerHorarioBaseDesdeFirestore } from "@/utils/horariosService";
import { obtenerHorariosDisponibles } from "@/utils/horarios";
import { crearProductoDeposito } from "@/utils/posHelpers";
import DepositCheckout from "@/components/pos/DepositCheckout.vue";

export default {
  name: "CreateCita",
  components: {
    DepositCheckout,
  },

  props: {
    enfermeros: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      requierePago: false,
      depositoPresencial: 0,
      mostrarMiniPos: false,
      depositoConfirmado: false,
      depositoMetodo: "efectivo",
      depositoProducto: {
        id: "",
        nombre: "",
        precio: 0,
        cantidad: 1,
      },
      pacientes: [],
      filtroPaciente: "",
      pacientesFiltrados: [],
      pacienteSel: null,
      serviciosDisponibles: [],
      horasDisponibles: [],
      disponibilidadSlots: {},
      enfermerosActivos: [],
      formCita: {
        pacienteId: "",
        pacienteNombre: "",
        servicios: [],
        fechaCita: "",
        hora: "",
        enfermero: "",
        duracion: 0,
        notaInterna: "",
        sincronizarAgenda: false,
        enviarRecordatorio: false,
      },
    };
  },
  computed: {
    totalServicios() {
      return (this.formCita.servicios || []).reduce(
        (acc, s) => acc + Number(s.precio || s.precioFinal || 0),
        0
      );
    },
    saldoPendienteEstimado() {
      if (!this.requierePago) return 0;
      const deposito = Number(this.depositoPresencial || 0);
      return Math.max(this.totalServicios - deposito, 0);
    },
    esteticistasDisponibles() {
      const mapa = new Map(
        (this.enfermerosActivos || []).map((enf) => [
          enf.id,
          `${enf.nombre || ""} ${enf.apellido || ""}`.trim() || enf.id,
        ])
      );
      const ids = this.formCita.hora
        ? this.disponibilidadSlots[this.formCita.hora] || []
        : (this.enfermerosActivos || []).map((enf) => enf.id);
      return ids.map((id) => ({
        id,
        nombre: mapa.get(id) || id,
      }));
    },
    nombreEnfermeroSeleccionado() {
      if (!this.formCita.enfermero) return "Automático";
      const found = (this.enfermerosActivos || []).find(
        (enf) => enf.id === this.formCita.enfermero
      );
      return found
        ? `${found.nombre || ""} ${found.apellido || ""}`.trim()
        : this.formCita.enfermero;
    },
  },

  methods: {
    async emitirCitaNueva() {
      console.log(" Emitiendo cita...");
      if (
        !this.formCita.pacienteId ||
        !this.formCita.hora ||
        !this.formCita.fechaCita
      ) {
        alert("Faltan datos (paciente, fecha, hora)");
        return;
      }
      if (Number(this.depositoPresencial) > 0 && !this.depositoConfirmado) {
        const continuar = confirm(
          "Tienes un depósito ingresado sin confirmar en el mini POS. ¿Quieres continuar sin registrarlo?"
        );
        if (!continuar) return;
      }

      const horaFin = this.sumarMinutos(
        this.formCita.hora,
        this.formCita.duracion
      );

      this.$emit("cita-creada", {
        pacienteId: this.formCita.pacienteId,
        paciente: this.pacienteSel,
        fecha: this.formCita.fechaCita,
        hora: this.formCita.hora,
        horaFin: horaFin,
        servicios: this.formCita.servicios,
        duracion: this.formCita.duracion,
        enfermeroId: this.formCita.enfermero,
        requierePago: this.requierePago,
        depositoPresencial: this.depositoPresencial || 0,
        depositoConfirmado: this.depositoConfirmado,
        depositoMetodo: this.depositoMetodo,
        notaInterna: this.formCita.notaInterna,
        sincronizarAgenda: this.formCita.sincronizarAgenda,
        enviarRecordatorio: this.formCita.enviarRecordatorio,
      });

      if (this.requierePago) {
        try {
          const crearFacturaPendiente = httpsCallable(
            functions,
            "crearFacturaPendiente"
          );

          await crearFacturaPendiente({
            pacienteId: this.formCita.pacienteId,
            citaId: "idCita",
            descripcion: `Pago pendiente por cita del ${this.formCita.fechaCita}`,
            servicios: this.formCita.servicios,
          });

          console.log("Factura pendiente registrada");
        } catch (err) {
          console.error(" Error al registrar factura pendiente:", err.message);
        }
      }
    },
    resetFormularioCita() {
      this.formCita = {
        pacienteId: "",
        pacienteNombre: "",
        servicios: [],
        fechaCita: "",
        hora: "",
        enfermero: "",
        duracion: 0,
        notaInterna: "",
        sincronizarAgenda: false,
        enviarRecordatorio: false,
      };
      this.depositoPresencial = 0;
      this.depositoConfirmado = false;
      this.depositoMetodo = "efectivo";
      this.mostrarMiniPos = false;
      this.depositoProducto = {
        id: "",
        nombre: "",
        precio: 0,
        cantidad: 1,
      };
      this.pacienteSel = null;
      this.searchPaciente = "";
      this.pacientesFiltrados = [];
      this.horasDisponibles = [];
    },

    async cargarPacientes() {
      const querySnapshot = await getDocs(collection(db, "pacientes"));
      this.pacientes = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },

    async seleccionarPaciente(paciente) {
      this.filtroPaciente = "";
      this.pacientesFiltrados = [];

      const pacienteRef = doc(db, "pacientes", paciente.id);
      const pacienteDoc = await getDoc(pacienteRef);

      if (!pacienteDoc.exists()) {
        alert(" Paciente no encontrado.");
        return;
      }

      const pacienteData = pacienteDoc.data();

      this.pacienteSel = {
        id: paciente.id,
        uid: pacienteData.uid || paciente.id,
        nombre: pacienteData.nombre || "",
        apellido: pacienteData.apellido || "",
        telefono: pacienteData.telefono || "",
        email: pacienteData.email || "",
        fullName: pacienteData.fullName || "",
      };

      this.formCita.pacienteId = this.pacienteSel.uid;
      this.formCita.pacienteNombre =
        this.pacienteSel.fullName ||
        `${this.pacienteSel.nombre} ${this.pacienteSel.apellido}`.trim();
    },

    buscarPacientes() {
      const filtro = this.filtroPaciente.toLowerCase();
      if (filtro === "") {
        this.pacientesFiltrados = [];
        return;
      }
      this.pacientesFiltrados = this.pacientes.filter(
        (paciente) =>
          (paciente.nombre || "").toLowerCase().includes(filtro) ||
          (paciente.apellido || "").toLowerCase().includes(filtro)
      );
    },

    abrirMiniPos() {
      const monto = Number(this.depositoPresencial || 0);
      if (monto <= 0) {
        alert("Ingresa un monto de depósito válido antes de abrir el mini POS.");
        return;
      }
      if (!this.pacienteSel) {
        alert("Selecciona un paciente antes de registrar el depósito.");
        return;
      }
      this.depositoProducto = crearProductoDeposito({
        citaId: null,
        paciente: this.pacienteSel,
        monto,
      });
      this.mostrarMiniPos = true;
    },
    cerrarMiniPos() {
      this.mostrarMiniPos = false;
    },
    confirmarDeposito({ monto, metodo }) {
      const montoSeguro = Math.max(0, Number(monto) || 0);
      if (montoSeguro <= 0) {
        alert("El depósito debe ser mayor a cero.");
        return;
      }
      this.depositoPresencial = montoSeguro;
      this.depositoConfirmado = true;
      this.depositoMetodo = metodo || "efectivo";
      this.mostrarMiniPos = false;
    },

    async cargarServicios() {
      const snap = await getDocs(collection(db, "productos"));
      this.serviciosDisponibles = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.tipo === "servicio" && p.activo !== false);
    },
    recalcularDuracion() {
      this.formCita.duracion = this.formCita.servicios.reduce(
        (acc, s) => acc + (s.duracion || 0),
        0
      );

      if (!this.formCita.fechaCita) return;

      this.$nextTick(() => {
        this.calcularHorasDisponiblesAdmin();
      });
    },

    async calcularHorasDisponibles() {
      if (
        !this.formCita.fechaCita ||
        !this.formCita.enfermero ||
        !this.formCita.duracion
      ) {
        this.horasDisponibles = [];
        return;
      }
      const enfermerosSnap = await getDocs(collection(db, "enfermeros"));
      const enfermerosArray = enfermerosSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const horarioBase = await obtenerHorarioBaseDesdeFirestore(
        enfermerosArray
      );

      const disponibilidad = obtenerHorariosDisponibles({
        fecha: this.formCita.fechaCita,
        duracion: this.formCita.duracion,
        citas: [],
        horarioBase,
      });

      const slots = Object.keys(disponibilidad).filter((slot) =>
        disponibilidad[slot].includes(this.formCita.enfermero)
      );

      this.horasDisponibles = slots;
    },

    async calcularHorasDisponiblesAdmin() {
      const fecha = this.formCita.fechaCita;
      const servicios = this.formCita.servicios;

      if (!fecha || !Array.isArray(servicios) || servicios.length === 0) {
        console.warn("❗ No hay servicios seleccionados o fecha vacía");
        this.horasDisponibles = [];
        return;
      }

      const duracion = servicios.reduce((acc, s) => acc + (s.duracion || 0), 0);
      if (!duracion) {
        console.warn("❗ Duración inválida");
        this.horasDisponibles = [];
        return;
      }

      const enfermerosSnap = await getDocs(collection(db, "enfermeros"));
      const enfermeros = enfermerosSnap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .filter((e) => e.activo !== false);

      if (enfermeros.length === 0) {
        console.warn(" No hay enfermeros activos");
        this.horasDisponibles = [];
        return;
      }
      this.enfermerosActivos = enfermeros;

      const horarioBase = await obtenerHorarioBaseDesdeFirestore(enfermeros);

      const citasSnap = await getDocs(collection(db, "citasGlobales"));
      const citas = citasSnap.docs
        .map((d) => d.data())
        .filter((c) => c.fecha === fecha);

      const disponibilidad = obtenerHorariosDisponibles({
        fecha,
        duracion,
        citas,
        horarioBase,
      });

      this.disponibilidadSlots = disponibilidad;
      this.horasDisponibles = Object.keys(disponibilidad);

      if (!this.formCita.enfermero && this.horasDisponibles.length > 0) {
        const primeraHora = this.horasDisponibles[0];
        const enfermeroDisponible = disponibilidad[primeraHora]?.[0] || null;
        if (enfermeroDisponible) {
          this.formCita.enfermero = enfermeroDisponible;
          console.log(
            " Enfermero asignado automáticamente:",
            enfermeroDisponible
          );
        }
      }

      console.log(" Horas disponibles:", this.horasDisponibles);
    },

    sumarMinutos(hora, minutos) {
      const [h, m] = hora.split(":").map(Number);
      const totalMin = h * 60 + m + minutos;
      const newH = Math.floor(totalMin / 60);
      const newM = totalMin % 60;
      return `${newH.toString().padStart(2, "0")}:${newM
        .toString()
        .padStart(2, "0")}`;
    },
    convertirHoraAMPMa24(hora) {
      try {
        if (!hora || typeof hora !== "string") {
          console.warn("Hora no válida:", hora);
          return "00:00";
        }

        // Extrae solo "7:00 PM" desde "7:00 PM (2 disponibles)" u otras variantes
        const match = hora.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!match) {
          console.warn("No se pudo extraer hora AM/PM válida:", hora);
          return "00:00";
        }

        let [, rawHour, rawMinute, rawModifier] = match;
        let hours = parseInt(rawHour, 10);
        const minutes = parseInt(rawMinute, 10);
        const modifier = rawModifier.toUpperCase();

        if (modifier === "PM" && hours !== 12) {
          hours += 12;
        }
        if (modifier === "AM" && hours === 12) {
          hours = 0;
        }

        return `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
      } catch (err) {
        console.error(
          " Error inesperado en convertirHoraAMPMa24:",
          err,
          "Valor recibido:",
          hora
        );
        return "00:00";
      }
    },

    formatearHora24aAMPM(hora24) {
      if (!hora24 || typeof hora24 !== "string" || !hora24.includes(":"))
        return "Hora inválida";

      const [hourStr, minute] = hora24.split(":");
      let hour = parseInt(hourStr, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;

      return `${hour}:${minute} ${ampm}`;
    },
    formatearMonto(valor) {
      const numero = Number(valor || 0);
      return new Intl.NumberFormat("es-PR", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(numero);
    },
  },

  async mounted() {
    await this.cargarPacientes();
    await this.cargarServicios();
  },
};
</script>

<style scoped>
@keyframes panelIn {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.create-cita {
  color: var(--ink);
  font-family: "Trebuchet MS", "Verdana", sans-serif;
  animation: panelIn 0.45s ease;
}

.create-cita .serif-title {
  font-family: "Iowan Old Style", "Palatino Linotype", "Palatino", "Georgia", serif;
  letter-spacing: -0.02em;
}

.panel-card {
  border-radius: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
  backdrop-filter: blur(6px);
}
</style>
